import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  IconUser, IconCamera, IconDeviceFloppy, IconEye, IconEyeOff,
  IconLayout, IconSun, IconMoon, IconDeviceLaptop, IconCheck,
  IconArrowsMaximize, IconLayoutColumns, IconScreenShare, IconDeviceDesktop, IconMaximize,
  IconTextResize,
} from '@tabler/icons-react';
import api from '../api/api';
import { applySettings, loadSettings } from '../components/ThemeSettings';

export default function MyPage({ me, setMe, showToast }) {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'profile';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [nickname, setNickname] = useState(me?.nickname || '');
  const [color, setColor] = useState(me?.color || '');
  const [darkColor, setDarkColor] = useState(me?.darkColor || '');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  // 레이아웃 설정
  const [layoutSettings, setLayoutSettings] = useState(() => loadSettings());

  const updateLayout = (key, value) => {
    const next = { ...layoutSettings, [key]: value };
    setLayoutSettings(next);
    localStorage.setItem('tabler-theme', JSON.stringify(next));
    applySettings(next);
  };

  const THEME_OPTIONS = [
    { value: 'auto', label: '자동', desc: 'OS 설정에 따름', icon: <IconDeviceLaptop size={28} /> },
    { value: 'light', label: '화이트', desc: '밝은 화이트 테마', icon: <IconSun size={28} /> },
    { value: 'dark', label: '다크', desc: '어두운 다크 테마', icon: <IconMoon size={28} /> },
  ];

  const WIDTH_OPTIONS = [
    { value: '1320px', label: '1320', desc: '기본', icon: <IconLayoutColumns size={24} /> },
    { value: '1600px', label: '1600', desc: '넓게', icon: <IconScreenShare size={24} /> },
    { value: '1920px', label: '1920', desc: 'Full HD', icon: <IconDeviceDesktop size={24} /> },
    { value: '2560px', label: '2560', desc: '2K', icon: <IconMaximize size={24} /> },
    { value: '100%',   label: '100%', desc: '제한없음', icon: <IconArrowsMaximize size={24} /> },
  ];

  const handleSave = async () => {
    if (password && password.length < 4) { showToast('비밀번호는 4자 이상이어야 합니다.', 'danger'); return; }
    if (password && password !== passwordConfirm) { showToast('비밀번호가 일치하지 않습니다.', 'danger'); return; }

    setSaving(true);
    try {
      const body = { nickname, color, darkColor };
      if (password) body.password = password;
      const res = await api.patch('/auth/me', body);
      setMe(res.data);
      setPassword('');
      setPasswordConfirm('');
      showToast('프로필이 저장되었습니다.', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || '저장에 실패했습니다.', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleProfileImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { showToast('이미지는 5MB 이하만 가능합니다.', 'danger'); return; }

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const res = await api.patch('/auth/me', { profileImage: reader.result });
        setMe(res.data);
        showToast('프로필 이미지가 변경되었습니다.', 'success');
      } catch {
        showToast('이미지 변경에 실패했습니다.', 'danger');
      }
    };
    reader.readAsDataURL(file);
  };

  const tabs = [
    { id: 'profile', icon: IconUser, label: '프로필' },
    { id: 'layout',  icon: IconLayout, label: '레이아웃' },
  ];

  return (
    <div className="row">
      <div className="col-12">
        <div className="mb-3">
          <div className="text-muted small">설정</div>
          <h2 className="mb-0" style={{ fontSize: 20, fontWeight: 700 }}>마이페이지</h2>
        </div>

        <div className="row g-3">
          {/* 사이드 탭 */}
          <div className="col-12 col-md-3">
            <div className="card">
              <div className="card-body p-2">
                {tabs.map(t => (
                  <button
                    key={t.id}
                    className={`btn btn-sm w-100 text-start d-flex align-items-center gap-2 mb-1 ${activeTab === t.id ? 'btn-primary' : 'btn-ghost-secondary'}`}
                    onClick={() => setActiveTab(t.id)}
                    style={{ fontWeight: activeTab === t.id ? 600 : 400 }}
                  >
                    <t.icon size={16} /> {t.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* 콘텐츠 */}
          <div className="col-12 col-md-9">

            {/* 프로필 탭 */}
            {activeTab === 'profile' && (
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title d-flex align-items-center gap-2">
                    <IconUser size={20} /> 프로필 설정
                  </h3>
                </div>
                <div className="card-body">
                  {/* 프로필 이미지 */}
                  <div className="text-center mb-4">
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      {me?.profileImage ? (
                        <img src={me.profileImage} alt="프로필" style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{
                          width: 80, height: 80, borderRadius: '50%',
                          background: 'var(--tblr-primary-lt, #e7f0fd)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 28, fontWeight: 700, color: 'var(--tblr-primary)',
                        }}>
                          {me?.name?.[0] || 'U'}
                        </div>
                      )}
                      <label style={{
                        position: 'absolute', bottom: 0, right: 0,
                        width: 28, height: 28, borderRadius: '50%',
                        background: 'var(--tblr-primary)', color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', border: '2px solid #fff',
                      }}>
                        <IconCamera size={14} />
                        <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleProfileImage} />
                      </label>
                    </div>
                    <div className="mt-2 text-muted small">{me?.email}</div>
                  </div>

                  {/* 이름 (읽기 전용) */}
                  <div className="mb-3">
                    <label className="form-label">이름</label>
                    <input type="text" className="form-control form-control-sm" value={me?.name || ''} disabled
                      style={{ background: 'var(--tblr-bg-surface-secondary, #f4f6fa)' }} />
                  </div>

                  {/* 닉네임 */}
                  <div className="mb-3">
                    <label className="form-label">닉네임
                      <small className="text-muted ms-1">(없으면 이름으로 표시)</small>
                    </label>
                    <input type="text" className="form-control form-control-sm"
                      placeholder="닉네임을 입력하세요"
                      value={nickname}
                      onChange={e => setNickname(e.target.value)} />
                  </div>

                  {/* 글씨색 (라이트/다크) */}
                  <div className="mb-3">
                    <label className="form-label">글씨색</label>
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                      <div className="d-flex align-items-center gap-1">
                        <span style={{ fontSize: 11, color: 'var(--tblr-muted)', width: 40 }}>라이트</span>
                        <input type="color" value={color || '#000000'}
                          onChange={e => setColor(e.target.value)}
                          style={{ width: 32, height: 28, padding: 0, border: '1px solid var(--tblr-border-color, #dee2e6)', borderRadius: 4, cursor: 'pointer' }} />
                        <span style={{ fontSize: 11, color: color || 'var(--tblr-muted)' }}>{color || '없음'}</span>
                        {color && (
                          <button className="btn btn-sm btn-ghost-secondary" style={{ padding: '0 4px', fontSize: 10 }}
                            onClick={() => setColor('')}>✕</button>
                        )}
                      </div>
                      <div className="d-flex align-items-center gap-1">
                        <span style={{ fontSize: 11, color: 'var(--tblr-muted)', width: 40 }}>다크</span>
                        <input type="color" value={darkColor || '#ffffff'}
                          onChange={e => setDarkColor(e.target.value)}
                          style={{ width: 32, height: 28, padding: 0, border: '1px solid var(--tblr-border-color, #dee2e6)', borderRadius: 4, cursor: 'pointer' }} />
                        <span style={{ fontSize: 11, color: 'var(--tblr-muted)' }}>{darkColor || '없음'}</span>
                        {darkColor && (
                          <button className="btn btn-sm btn-ghost-secondary" style={{ padding: '0 4px', fontSize: 10 }}
                            onClick={() => setDarkColor('')}>✕</button>
                        )}
                      </div>
                      {(color || darkColor) && (
                        <div className="d-flex gap-1">
                          <span style={{ color: color || '#333', background: '#fff', border: '1px solid #dee2e6', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>밝은배경</span>
                          <span style={{ color: darkColor || '#ccc', background: '#1e293b', border: '1px solid #334155', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>어두운배경</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 비밀번호 변경 */}
                  <div className="mb-3">
                    <label className="form-label">새 비밀번호 <small className="text-muted">(변경 시에만 입력)</small></label>
                    <div style={{ position: 'relative' }}>
                      <input type={showPw ? 'text' : 'password'} className="form-control form-control-sm"
                        placeholder="새 비밀번호" value={password} onChange={e => setPassword(e.target.value)}
                        style={{ paddingRight: 40 }} />
                      <button type="button" onClick={() => setShowPw(v => !v)}
                        style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--tblr-muted)' }}>
                        {showPw ? <IconEyeOff size={17} /> : <IconEye size={17} />}
                      </button>
                    </div>
                  </div>

                  {password && (
                    <div className="mb-3">
                      <label className="form-label">비밀번호 확인</label>
                      <input type={showPw ? 'text' : 'password'} className="form-control form-control-sm"
                        placeholder="비밀번호 확인" value={passwordConfirm} onChange={e => setPasswordConfirm(e.target.value)} />
                    </div>
                  )}

                  {/* 계정 정보 */}
                  <div className="mb-4 p-3 rounded" style={{ background: 'var(--tblr-bg-surface-secondary, #f8f9fa)' }}>
                    <div className="row text-muted small">
                      <div className="col-6">역할: <strong>{me?.role}</strong></div>
                      <div className="col-6">상태: <strong>{me?.status}</strong></div>
                      <div className="col-6 mt-1">가입일: {me?.createdAt ? new Date(me.createdAt).toLocaleDateString('ko-KR') : '-'}</div>
                      <div className="col-6 mt-1">최근 로그인: {me?.lastLoginAt ? new Date(me.lastLoginAt).toLocaleString('ko-KR') : '-'}</div>
                    </div>
                  </div>

                  <button className="btn btn-sm btn-primary d-flex align-items-center gap-1" onClick={handleSave} disabled={saving}>
                    <IconDeviceFloppy size={16} />
                    {saving ? '저장 중...' : '저장'}
                  </button>
                </div>
              </div>
            )}

            {/* 레이아웃 설정 탭 */}
            {activeTab === 'layout' && (
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title mb-0">레이아웃 설정</h3>
                  <div className="card-options text-muted small">
                    화면 테마와 가로 사이즈를 설정하세요
                  </div>
                </div>
                <div className="card-body">

                  {/* 테마 */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold mb-3" style={{ fontSize: 15 }}>테마</label>
                    <div className="row g-3">
                      {THEME_OPTIONS.map(opt => {
                        const isSelected = layoutSettings.theme === opt.value;
                        return (
                          <div key={opt.value} className="col-12 col-sm-4">
                            <div
                              onClick={() => updateLayout('theme', opt.value)}
                              style={{
                                border: isSelected
                                  ? '2px solid var(--tblr-primary, #206bc4)'
                                  : '2px solid var(--tblr-border-color, #e6e7e9)',
                                borderRadius: 10,
                                padding: '20px 16px',
                                cursor: 'pointer',
                                textAlign: 'center',
                                background: isSelected
                                  ? 'rgba(32,107,196,0.06)'
                                  : 'var(--tblr-bg-surface, #fff)',
                                transition: 'border-color 0.15s, background 0.15s',
                                position: 'relative',
                              }}
                            >
                              {isSelected && (
                                <span className="badge bg-primary"
                                  style={{ position: 'absolute', top: 8, right: 8 }}>
                                  <IconCheck size={11} className="me-1" />선택
                                </span>
                              )}
                              <div style={{ color: isSelected ? 'var(--tblr-primary, #206bc4)' : 'var(--tblr-muted, #9aa0ac)', marginBottom: 8 }}>
                                {opt.icon}
                              </div>
                              <div className="fw-semibold mb-1" style={{ fontSize: 15 }}>{opt.label}</div>
                              <div className="text-muted" style={{ fontSize: 12 }}>{opt.desc}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <hr className="my-4" />

                  {/* 컴포넌트 크기 */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold mb-1" style={{ fontSize: 15 }}>
                      <IconTextResize size={18} className="me-1" style={{ verticalAlign: '-3px' }} />
                      컴포넌트 크기
                    </label>
                    <div className="text-muted small mb-3">입력, 버튼, 셀렉트 등의 크기를 조절합니다</div>
                    <div className="row g-2 row-cols-5">
                      {[
                        { value: 'xs', label: 'XS', desc: '매우 작게' },
                        { value: 'sm', label: 'SM', desc: '작게' },
                        { value: 'default', label: '보통', desc: '기본' },
                        { value: 'lg', label: 'LG', desc: '크게' },
                        { value: 'xl', label: 'XL', desc: '매우 크게' },
                      ].map(opt => {
                        const isSelected = (layoutSettings.componentSize || 'sm') === opt.value;
                        return (
                          <div key={opt.value} className="col">
                            <div
                              onClick={() => updateLayout('componentSize', opt.value)}
                              style={{
                                border: isSelected
                                  ? '2px solid var(--tblr-primary, #206bc4)'
                                  : '2px solid var(--tblr-border-color, #e6e7e9)',
                                borderRadius: 10,
                                padding: '14px 8px',
                                cursor: 'pointer',
                                textAlign: 'center',
                                background: isSelected
                                  ? 'rgba(32,107,196,0.06)'
                                  : 'var(--tblr-bg-surface, #fff)',
                                transition: 'border-color 0.15s, background 0.15s',
                                position: 'relative',
                              }}
                            >
                              {isSelected && (
                                <span className="badge bg-primary"
                                  style={{ position: 'absolute', top: 6, right: 6, fontSize: 10 }}>
                                  <IconCheck size={10} />
                                </span>
                              )}
                              <div className="fw-semibold" style={{ fontSize: 14 }}>{opt.label}</div>
                              <div className="text-muted" style={{ fontSize: 11 }}>{opt.desc}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <hr className="my-4" />

                  {/* 가로 사이즈 */}
                  <div className="mb-2">
                    <label className="form-label fw-semibold mb-3" style={{ fontSize: 15 }}>가로 사이즈</label>
                    <div className="row g-2 row-cols-5">
                      {WIDTH_OPTIONS.map(opt => {
                        const isSelected = layoutSettings.maxWidth === opt.value;
                        return (
                          <div key={opt.value} className="col">
                            <div
                              onClick={() => updateLayout('maxWidth', opt.value)}
                              style={{
                                border: isSelected
                                  ? '2px solid var(--tblr-primary, #206bc4)'
                                  : '2px solid var(--tblr-border-color, #e6e7e9)',
                                borderRadius: 10,
                                padding: '14px 8px',
                                cursor: 'pointer',
                                textAlign: 'center',
                                background: isSelected
                                  ? 'rgba(32,107,196,0.06)'
                                  : 'var(--tblr-bg-surface, #fff)',
                                transition: 'border-color 0.15s, background 0.15s',
                                position: 'relative',
                              }}
                            >
                              {isSelected && (
                                <span className="badge bg-primary"
                                  style={{ position: 'absolute', top: 6, right: 6, fontSize: 10 }}>
                                  <IconCheck size={10} />
                                </span>
                              )}
                              <div style={{ color: isSelected ? 'var(--tblr-primary, #206bc4)' : 'var(--tblr-muted, #9aa0ac)', marginBottom: 6 }}>
                                {opt.icon}
                              </div>
                              <div className="fw-semibold" style={{ fontSize: 14 }}>{opt.label}</div>
                              <div className="text-muted" style={{ fontSize: 11 }}>{opt.desc}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* 너비 프리뷰 바 */}
                    <div className="mt-3 rounded overflow-hidden"
                      style={{ height: 6, background: 'var(--tblr-border-color, #e6e7e9)' }}>
                      <div style={{
                        height: '100%',
                        background: 'var(--tblr-primary, #206bc4)',
                        width: ({'1320px':'30%','1600px':'45%','1920px':'60%','2560px':'80%','100%':'100%'})[layoutSettings.maxWidth] || '30%',
                        transition: 'width 0.3s ease',
                        borderRadius: 3,
                      }} />
                    </div>
                    <div className="text-muted small mt-1 text-center">
                      {layoutSettings.maxWidth === '100%' ? '화면 전체 너비 사용' : `최대 ${layoutSettings.maxWidth} 사용`}
                    </div>
                  </div>

                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
